import { createResource, createSignal, Show, type JSX } from 'solid-js';
import { A } from '@solidjs/router';
import {
  fetchMyStrainRating,
  fetchStrainRatingSummary,
  setStrainRating,
  removeStrainRating,
} from '@lib/supabase/ratings';
import { authStore } from '@stores/session';

// 5-star rating widget for a single strain. Two modes:
//
//   * signed-out → read-only display of the community average + count,
//     with a "sign in to rate" hint
//   * signed-in  → interactive stars that hover-preview, click to set
//     or re-set, and click-current-to-remove
//
// `compact` collapses the layout for surfaces like /my-strains cards
// where the full average/count description would dominate the card.
// In compact mode we skip the description block and use slightly
// smaller stars; the click behaviour is identical.
//
// Optimistic update: clicking flips a local override immediately so
// the user sees feedback before the network round-trip completes. The
// override clears on refetch, falling back to the authoritative
// server value (which catches any failed mutation).

export default function StarRating(props: {
  strainId: string;
  compact?: boolean;
}): JSX.Element {
  const [summary, { refetch: refetchSummary }] = createResource(
    () => props.strainId,
    fetchStrainRatingSummary,
  );
  const [myRating, { refetch: refetchMine }] = createResource(
    () => ({ strainId: props.strainId, user: authStore.profile()?.id ?? null }),
    ({ strainId }) => fetchMyStrainRating(strainId),
  );

  const [hover, setHover] = createSignal<number | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  // Sentinel signal for optimistic state. `undefined` means "no
  // override, use the resource". A number is the user's pending click;
  // `null` is a pending clear.
  const [optimistic, setOptimistic] = createSignal<number | null | undefined>(undefined);

  // Effective rating reads from the optimistic override first,
  // falling back to the resource. Used by displayValue and aria.
  const effectiveRating = (): number | null => {
    const o = optimistic();
    if (o !== undefined) return o;
    return myRating() ?? null;
  };

  async function setRating(stars: number) {
    if (!authStore.user()) return;
    const current = effectiveRating();
    // Clicking the already-selected star clears the rating — standard
    // star-rating UX.
    const next = current === stars ? null : stars;
    setOptimistic(next);
    setBusy(true);
    setError(null);
    try {
      if (next === null) {
        await removeStrainRating(props.strainId);
      } else {
        await setStrainRating(props.strainId, next);
      }
      await refetchMine();
      await refetchSummary();
      setOptimistic(undefined);
    } catch (err) {
      // Rollback so the UI matches reality. The resource value is
      // unchanged (we never refetched), so clearing the override
      // restores the pre-click state.
      setOptimistic(undefined);
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // How many stars to fill visually. Prefer hover-preview when the user
  // is actively pointing at a star; otherwise their committed rating
  // (with optimistic override); otherwise the rounded community
  // average; otherwise zero.
  const displayValue = (): number => {
    const h = hover();
    if (h != null) return h;
    const mine = effectiveRating();
    if (mine != null) return mine;
    const avg = summary()?.avg_stars;
    if (avg != null) return Math.round(avg);
    return 0;
  };

  const interactive = () => !!authStore.user();

  return (
    <div class="flex flex-col gap-1 items-start">
      <div
        class="flex items-center gap-0.5"
        role={interactive() ? 'radiogroup' : undefined}
        aria-label="Strain rating"
        onMouseLeave={() => setHover(null)}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            filled={n <= displayValue()}
            interactive={interactive()}
            selected={effectiveRating() === n}
            busy={busy()}
            compact={!!props.compact}
            onMouseEnter={() => interactive() && setHover(n)}
            onClick={() => interactive() && void setRating(n)}
            ariaLabel={`${n} star${n === 1 ? '' : 's'}`}
            ariaChecked={effectiveRating() === n}
          />
        ))}
      </div>
      <Show when={!props.compact}>
        <div class="text-xs text-brand-green/80">
          <Show
            when={summary() && summary()!.rating_count > 0}
            fallback={
              <Show
                when={interactive()}
                fallback={
                  <>
                    No ratings yet &ndash;{' '}
                    <A href="/sign-in" class="underline">sign in</A> to be the first.
                  </>
                }
              >
                No ratings yet &ndash; tap a star to be the first.
              </Show>
            }
          >
            <>
              <span class="font-display font-semibold text-brand-green">
                {Number(summary()!.avg_stars).toFixed(1)}
              </span>{' '}
              avg · {summary()!.rating_count} rating
              {summary()!.rating_count === 1 ? '' : 's'}
              <Show when={effectiveRating() != null}>
                <span class="ml-2 text-brand-moss">(you rated {effectiveRating()})</span>
              </Show>
            </>
          </Show>
        </div>
      </Show>
      <Show when={error()}>
        <p class="text-xs text-red-700" role="alert">{error()}</p>
      </Show>
    </div>
  );
}

function Star(props: {
  filled: boolean;
  interactive: boolean;
  selected: boolean;
  busy: boolean;
  compact: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  ariaLabel: string;
  ariaChecked: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role={props.interactive ? 'radio' : undefined}
      aria-label={props.ariaLabel}
      aria-checked={props.interactive ? props.ariaChecked : undefined}
      disabled={!props.interactive || props.busy}
      onMouseEnter={props.onMouseEnter}
      onClick={(e) => {
        // Stop the click bubbling — portfolio cards wrap the rating in
        // their action region, but a stray bubble could trigger any
        // ancestor link. Defensive but cheap.
        e.stopPropagation();
        props.onClick();
      }}
      class={`p-0.5 rounded ${
        props.interactive
          ? 'cursor-pointer hover:scale-110 transition-transform'
          : 'cursor-default'
      } disabled:cursor-not-allowed`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        class={`transition-colors ${props.compact ? 'w-4 h-4' : 'w-6 h-6'} ${
          props.filled ? 'text-brand-moss' : 'text-brand-hex'
        }`}
        fill={props.filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 0 0 .95.69h4.163c.969 0 1.371 1.24.588 1.81l-3.37 2.447a1 1 0 0 0-.363 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 0 0-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 0 0-.363-1.118L2.098 9.384c-.784-.57-.38-1.81.588-1.81h4.163a1 1 0 0 0 .95-.69l1.286-3.957Z"
        />
      </svg>
    </button>
  );
}
